/**
 * フォーム編集画面用文書情報取得カスタムフック
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';

import Log from '@/Logger';

import {
    ConnectionStatus,
    type FormDocument,
    type ItpcmdGetFormDocumentResponse,
    type ItpcmdRequest,
    type ItpcmdResponse,
    EWBCIError,
    EWBCIErrorType,
} from '@/feature/ewbci';
import { equalsDoc } from '../utils';

/**
 * 文書情報の設定オプション
 */
export interface FormDocumentState {
    docId: string;
    status: FormDocumentStateStatus;
    docInfo: FormDocument | null;
    error: Error | null;
    fetching: string | null;
    scheduled: boolean;
}

export const FormDocumentStateStatus = {
    None: 'none',
    Idle: 'idle',
    Loading: 'loading',
    Success: 'success',
    Error: 'error',
} as const;
export type FormDocumentStateStatus = (typeof FormDocumentStateStatus)[keyof typeof FormDocumentStateStatus];

type FormDocumentStateAction = {
    type: 'SET_DOCID',
    docId: string,
} | {
    type: 'FETCH_START',
} | {
    type: 'FETCH_SUCCESS',
    docInfo: FormDocument,
} | {
    type: 'SET_SCHEDULED',
    scheduled: boolean,
} | {
    type: 'FETCH_ERROR',
    error: Error,
};

const initialState = {
    docId: '',
    status: FormDocumentStateStatus.None,
    docInfo: null,
    error: null,
    fetching: null,
    scheduled: false,
};

/**
 * 文書情報フックのオプション
 */
export type FormDocumentOptions = {
    connectionStatus: ConnectionStatus;
    executeItpcmd: (itpcmd: ItpcmdRequest, requestId?: string) => Promise<ItpcmdResponse>;
    docId: string;
    interval?: number; // ミリ秒単位のポーリング間隔（未指定 or 0 は無効）
}

/**
 * 文書情報フックの戻り値
 */
export interface FormDocumentHooks {
    status: FormDocumentStateStatus;
    docInfo: FormDocument | null;
    error: Error | null;
    refresh: () => Promise<void>;
}

const reducer = (state: FormDocumentState, action: FormDocumentStateAction) => {
    const logger = Log.getLogger();
    const { docId } = state;
    switch (action.type) {
        case 'SET_DOCID':
            if (state.docId === action.docId) {
                return state; // 変更なし
            } else if (action.docId === '') {
                // 破棄
                logger.api.info("useFormDocument.setDocId", "changed", { from: state.docId, to: action.docId });
                return { ...state, docId: action.docId, status: FormDocumentStateStatus.None, docInfo: null, error: null, fetching: null, scheduled: false };
            } else {
                // 初期化
                logger.api.info("useFormDocument.setDocId", "changed", { from: state.docId, to: action.docId });
                return { ...state, docId: action.docId, status: FormDocumentStateStatus.Idle, docInfo: null, error: null, fetching: null, scheduled: false };
            }
        case 'FETCH_START':
            logger.api.info("useFormDocument.fetch", "start", { docId });
            return { ...state, status: FormDocumentStateStatus.Loading, error: null, fetching: docId, scheduled: false };

        case 'FETCH_SUCCESS':
            if (equalsDoc(state.docInfo, action.docInfo)) {
                logger.api.info("useFormDocument.fetch", "not modified", { docId });
                return { ...state, status: FormDocumentStateStatus.Success, fetching: null };
            } else {
                logger.api.info("useFormDocument.fetch", "finished", { docId });
                return { ...state, status: FormDocumentStateStatus.Success, docInfo: action.docInfo, fetching: null };
            }
        case 'FETCH_ERROR':
            const error = action.error;
            logger.api.info("useFormDocument.fetch", "error", { error });
            return { ...state, status: FormDocumentStateStatus.Error, error, fetching: null };

        case 'SET_SCHEDULED':
            return { ...state, scheduled: action.scheduled };
        /* istanbul ignore next: TypeScript型安全性により到達不可 */
        default: return state;
    }
}

/**
 * 文書情報の管理を担うカスタムフック
 * 
 * @param options 設定オプション
 * @returns 文書情報フックの戻り値
 */
export function useFormDocument(options: FormDocumentOptions) {
    // 依存引数
    const { connectionStatus, executeItpcmd, docId, interval } = options;
    // 状態
    const [state, dispatch] = useReducer(reducer, initialState);

    // タイマー管理とstatusの参照
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const statusRef = useRef<FormDocumentStateStatus>(state.status);

    // 状態変化を ref に同期
    statusRef.current = state.status;

    // タイマークリアユーティリティ
    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    // fetch処理（ガード付き）
    const fetchWithGuard = useCallback(async () => {
        const logger = Log.getLogger();
        // ガード条件
        if (docId === '') {
            logger.api.debug("useFormDocument.fetch", "skip", { reason: "no docId" });
            return;
        }
        if (statusRef.current === FormDocumentStateStatus.Loading && state.fetching === docId) {
            logger.api.debug("useFormDocument.fetch", "skip", { reason: "already loading", docId });
            return;
        }

        dispatch({ type: 'FETCH_START' });

        executeItpcmd({
            method: 'GET',
            query: `/form/${docId}`,
            body: JSON.stringify({
                property: "document"
            })
        }).then(res => {
            if (res.status === 200) {
                const body = res.body as ItpcmdGetFormDocumentResponse;
                dispatch({ type: 'FETCH_SUCCESS', docInfo: body.document });
            } else {
                dispatch({ type: 'FETCH_ERROR', error: new EWBCIError(`[useFormDocument] fetch error: [${res.status}] ${res.reason}`, EWBCIErrorType.EwbciError) });
            }
        }).catch(err => {
            if (err instanceof EWBCIError) {
                dispatch({ type: 'FETCH_ERROR', error: err });
            } else {
                dispatch({ type: 'FETCH_ERROR', error: new Error("[useFormDocument] fetch error", { cause: err }) });
            }
        });

    }, [docId, executeItpcmd, state.fetching]);
    const fetchWithGuardRef = useRef(fetchWithGuard);
    fetchWithGuardRef.current = fetchWithGuard;

    // refresh API
    const refresh = useCallback(async () => {
        const logger = Log.getLogger();
        logger.api.info("useFormDocument.refresh", "called", { docId });
        clearTimer();
        await fetchWithGuard();
    }, [clearTimer, fetchWithGuard, docId]);

    // docId変更時の状態更新
    useEffect(() => dispatch({ type: 'SET_DOCID', docId }), [docId]);

    // 接続状態変化時の処理
    useEffect(() => {
        const logger = Log.getLogger();
        if (connectionStatus === ConnectionStatus.CONNECTED && docId) {
            logger.ui.debug("useFormDocument", `Connection established, fetching document (${docId})`);
            // 接続確立時は即座にfetch
            fetchWithGuardRef.current();
        } else {
            clearTimer();
            logger.ui.debug("useFormDocument", `Connection status changed: ${connectionStatus} (docId: ${docId}) - cleared schedule`);
        }
    }, [connectionStatus, docId]);

    // fetch完了後のスケジューリング & エラーリトライ
    useEffect(() => {
        const logger = Log.getLogger();
        logger.ui.info("useFormDocument", `status: (${state.status})`);

        switch (state.status) {
            case FormDocumentStateStatus.Success: {
                if (connectionStatus === ConnectionStatus.CONNECTED && interval && interval > 0 && !state.scheduled) {
                    // 成功時は次の定期実行をスケジュール
                    clearTimer();
                    timerRef.current = setTimeout(() => {
                        fetchWithGuard();
                    }, interval);
                    dispatch({ type: 'SET_SCHEDULED', scheduled: true });
                    logger.api.info("useFormDocument.schedule", "scheduled next fetch after success", { afterMs: interval });
                }
                break;
            }
            case FormDocumentStateStatus.Error: {
                // エラー時は3秒後にリトライ
                if (connectionStatus === ConnectionStatus.CONNECTED && !state.scheduled) {
                    clearTimer();
                    timerRef.current = setTimeout(() => {
                        fetchWithGuard();
                    }, 3000);
                    logger.api.info("useFormDocument.schedule", "scheduled retry after error", { afterMs: 3000 });
                    dispatch({ type: 'SET_SCHEDULED', scheduled: true });
                }
                break;
            }
            default:
                break;
        }
    }, [state.status, connectionStatus, interval, state.scheduled, fetchWithGuard]);

    // クリーンアップ
    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, []);

    return {
        ...state,
        refresh,
    };
}
